import { todayIST } from "@templeos/shared";
import type { PosDB } from "./db.js";
import { chainHash, GENESIS } from "./hash.js";
import type { ApiCartLine, Payer, Payment, PosConfig, QueuedSale, SaleLine, SyncResult } from "./types.js";

export interface NewSale {
  counterCode: string;
  cashier: string;
  shiftId: string | null;
  cart: ApiCartLine[];
  lines: SaleLine[];
  payments: Payment[];
  payer?: Payer;
  total: number;
}
export interface ChainHead { n: number; hash: string }
/** The part of localStorage the queue uses; injected so tests can run without a browser. */
export interface MirrorStorage { getItem(key: string): string | null; setItem(key: string, value: string): void }

export const HEAD_MIRROR_KEY = "tos-pos-chain-head";
const ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ"; // no 0/O/1/I, easy to read aloud

export function randomSuffix(len = 6): string {
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  return [...bytes].map((b) => ALPHABET[b % ALPHABET.length]).join("");
}

/** Duplicate-proof sale reference: counter, business day, roll serial and a random tail. */
export const makeClientRef = (counterCode: string, day: string, seq: number, suffix: string): string =>
  `${counterCode}-${day.replace(/-/g, "")}-${String(seq).padStart(5, "0")}-${suffix}`;

class StaleHead extends Error {}

// One enqueue at a time per window; a second POS window on the same terminal is caught by the head re-check.
let tail: Promise<unknown> = Promise.resolve();
function serial<T>(fn: () => Promise<T>): Promise<T> {
  const run = tail.then(fn, fn);
  tail = run.catch(() => undefined);
  return run;
}

/**
 * Save a sale to the local queue before anything else happens (sub-300 ms checkout). Allocates the next roll serial
 * and chain position, hashes the sale onto the chain and mirrors the chain head to localStorage.
 */
export function enqueueSale(db: PosDB, input: NewSale, now: Date = new Date(), storage?: MirrorStorage): Promise<QueuedSale> {
  const paid = input.payments.reduce((s, p) => s + p.amount, 0);
  if (paid !== input.total) return Promise.reject(new Error(`Payments (${paid}) must equal the bill total (${input.total}).`));
  if (!input.cart.length) return Promise.reject(new Error("The bill is empty."));
  return serial(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const day = todayIST(now);
      const seqKey = `seq:${input.counterCode}:${day}`;
      const head = (await db.getMeta<ChainHead>("chainHead")) ?? { n: 0, hash: GENESIS };
      const seq = ((await db.getMeta<number>(seqKey)) ?? 0) + 1;
      const base = { ...input, clientRef: makeClientRef(input.counterCode, day, seq, randomSuffix()), n: head.n + 1, seq, day, createdAt: now.toISOString() };
      const hash = await chainHash(head.hash, base);
      const sale: QueuedSale = { ...base, prevHash: head.hash, hash, status: "pending", attempts: 0 };
      try {
        await db.transaction("rw", db.sales, db.meta, async () => {
          const cur = (await db.getMeta<ChainHead>("chainHead")) ?? { n: 0, hash: GENESIS };
          const curSeq = (await db.getMeta<number>(seqKey)) ?? 0;
          if (cur.hash !== head.hash || curSeq !== seq - 1) throw new StaleHead();
          await db.sales.add(sale);
          await db.setMeta("chainHead", { n: sale.n, hash });
          await db.setMeta(seqKey, seq);
        });
        storage?.setItem(HEAD_MIRROR_KEY, JSON.stringify({ n: sale.n, hash }));
        return sale;
      } catch (e) {
        if (!(e instanceof StaleHead)) throw e;
      }
    }
    throw new Error("Another POS window is billing on this terminal. Use one window per counter.");
  });
}

/**
 * Compare the last saved sale, the IndexedDB chain head and its localStorage mirror. Any mismatch means sales were
 * deleted (from the end of the queue too) or the database was replaced. A sale marked synced without a receipt
 * number was flipped by hand to hide it from the upload queue. An unreadable mirror is itself an alert; none of
 * this blocks signing in, the supervisor decides.
 */
export async function checkMirror(db: PosDB, storage: MirrorStorage): Promise<{ ok: boolean; message?: string }> {
  const head = (await db.getMeta<ChainHead>("chainHead")) ?? { n: 0, hash: GENESIS };
  const last = await db.sales.orderBy("n").last();
  if ((last?.n ?? 0) !== head.n || (last && last.hash !== head.hash)) {
    return { ok: false, message: `Local sales store was altered: the chain records ${head.n} sales but the last saved sale is ${last?.n ?? 0}. Call a supervisor before billing.` };
  }
  const flipped = await db.sales.filter((s) => s.status === "synced" && !s.receiptId).count();
  if (flipped) return { ok: false, message: `${flipped} sale(s) are marked as uploaded but carry no receipt number. Call a supervisor before billing.` };
  let mirror: ChainHead | null = null;
  try {
    const raw = storage.getItem(HEAD_MIRROR_KEY);
    mirror = raw ? (JSON.parse(raw) as ChainHead) : null;
    if (mirror && (!Number.isInteger(mirror.n) || typeof mirror.hash !== "string")) throw new Error("bad mirror");
  } catch {
    return { ok: false, message: "The backup copy of the sales chain on this terminal is unreadable. Call a supervisor before billing." };
  }
  if (!mirror || head.n > mirror.n) {
    storage.setItem(HEAD_MIRROR_KEY, JSON.stringify(head));
    return { ok: true };
  }
  if (mirror.n > head.n || mirror.hash !== head.hash) {
    return { ok: false, message: `Local sales store was altered: expected ${mirror.n} saved sales, found ${head.n}. Call a supervisor before billing.` };
  }
  return { ok: true };
}

/**
 * This terminal's name for its hash chain on the server (POS request 5 to T1b): made once per local store, so a store
 * that is wiped or replaced shows up as a new terminal while the old one's missing sales stay visible.
 */
export function terminalId(db: PosDB): Promise<string> {
  return db.transaction("rw", db.meta, async () => {
    const id = await db.getMeta<string>("terminalId");
    if (id) return id;
    const made = crypto.randomUUID();
    await db.setMeta("terminalId", made);
    return made;
  });
}

/** A sale's place on the chain, sent with every upload so the server can spot sales that never arrived. */
export const chainLink = (s: QueuedSale, terminal: string) => ({ terminalId: terminal, n: s.n, prevHash: s.prevHash, hash: s.hash });

/** The chain head and every sale still waiting on this terminal, whoever sold it, for the heartbeat. */
export async function chainReport(db: PosDB): Promise<{ terminalId: string; n: number; hash: string; pending: number }> {
  const head = (await db.getMeta<ChainHead>("chainHead")) ?? { n: 0, hash: GENESIS };
  return { terminalId: await terminalId(db), n: head.n, hash: head.hash, pending: await db.sales.where("status").equals("pending").count() };
}

/** Pending sales of one cashier: the server books a synced sale to the uploading user's open shift. */
export const pendingSales = (db: PosDB, cashier: string, limit = 1000): Promise<QueuedSale[]> =>
  db.sales.where("status").equals("pending").filter((s) => s.cashier === cashier).sortBy("n").then((xs) => xs.slice(0, limit));

/** Chain positions from..to, in order (used to verify a batch and its anchor before upload). */
export const chainRange = (db: PosDB, from: number, to: number): Promise<QueuedSale[]> => db.sales.where("n").between(from, to, true, true).sortBy("n");

/**
 * Server answers that mean "not now" rather than "this bill is refused": a busy database, an unexpected error, or a
 * state a supervisor fixes (no open shift, counter assignment, device binding, inactive counter). Such sales stay
 * queued and are sent again; every other error code is a final refusal.
 */
export const RETRYABLE_CODES: ReadonlySet<string> = new Set(["RETRY", "INTERNAL", "NO_OPEN_SHIFT", "NOT_ASSIGNED", "DEVICE_NOT_AUTHORISED", "COUNTER_INACTIVE"]);
export const isRefusal = (code: string | undefined): boolean => !RETRYABLE_CODES.has(code ?? "INTERNAL");

/** Record the server's answer for one sale. ok and duplicate both mean the server holds it. */
export async function applyResult(db: PosDB, r: SyncResult, now: Date = new Date()): Promise<void> {
  const cur = await db.sales.get(r.clientRef);
  if (!cur) return;
  if (r.status === "error") {
    const error = r.error ?? { code: "INTERNAL", message: "No answer for this sale; it will be sent again." };
    await db.sales.update(r.clientRef, { status: isRefusal(error.code) ? "rejected" : "pending", attempts: cur.attempts + 1, error });
    return;
  }
  await db.sales.update(r.clientRef, {
    status: "synced", attempts: cur.attempts + 1, receiptId: r.receiptId, number: r.number, publicToken: r.publicToken ?? cur.publicToken,
    tickets: r.tickets ?? cur.tickets, error: undefined, syncedAt: now.toISOString(),
  });
}

export interface Tally { bills: number; tickets: number; amount: number; cash: number; upi: number; pending: number; pendingAmount: number; rejected: number }

/** Live tally of a shift from the local queue (includes offline sales); rejected and voided bills do not count. */
export function tally(sales: readonly QueuedSale[]): Tally {
  const t: Tally = { bills: 0, tickets: 0, amount: 0, cash: 0, upi: 0, pending: 0, pendingAmount: 0, rejected: 0 };
  for (const s of sales) {
    if (s.status === "rejected") { t.rejected++; continue; }
    if (s.voided) continue;
    t.bills++;
    t.amount += s.total;
    t.tickets += s.lines.filter((l) => l.kind === "ticket").reduce((n, l) => n + l.qty, 0);
    for (const p of s.payments) t[p.mode] += p.amount;
    if (s.status === "pending") { t.pending++; t.pendingAmount += s.total; }
  }
  return t;
}

/**
 * Tickets the cashier has issued this shift, for the shift quota: the server's count when the configuration was
 * requested (every terminal) plus this terminal's bills it had not counted then (still waiting, or synced since).
 * A count for another shift is ignored. `sales` are this shift's bills on this terminal.
 */
export function shiftTickets(quota: PosConfig["cashierQuota"], shiftId: string | null, sales: readonly QueuedSale[]): number {
  if (!quota || quota.shiftId !== shiftId) return tally(sales).tickets;
  return quota.sold + tally(sales.filter((s) => s.status === "pending" || (s.syncedAt ?? "") > quota.at)).tickets;
}

/** Quantity of each catalogue item already sold in the shift. */
export function soldByItem(sales: readonly QueuedSale[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of sales) {
    if (s.status === "rejected" || s.voided) continue;
    for (const l of s.lines) if (l.itemId) m.set(l.itemId, (m.get(l.itemId) ?? 0) + l.qty);
  }
  return m;
}

/** Units per item in bills still on this terminal only: the server's stock and quota figures do not include them yet. */
export const pendingByItem = (sales: readonly QueuedSale[]): Map<string, number> => soldByItem(sales.filter((s) => s.status === "pending"));

export const shiftSales = (db: PosDB, shiftId: string): Promise<QueuedSale[]> => db.sales.where("shiftId").equals(shiftId).sortBy("n");

/**
 * Find one of the cashier's own current-shift sales by its reference or one of its ticket codes (SOW Module 4: a
 * cashier sees only the active shift, never an earlier cashier's bills on the same terminal).
 */
export async function findLocal(db: PosDB, code: string, own: { shiftId: string; cashier: string }): Promise<QueuedSale | undefined> {
  const c = code.trim().toUpperCase();
  const mine = (s: QueuedSale) => s.shiftId === own.shiftId && s.cashier === own.cashier;
  const hit = await db.sales.get(c);
  if (hit) return mine(hit) ? hit : undefined;
  return db.sales.where("shiftId").equals(own.shiftId).filter((s) => mine(s) && (Boolean(s.tickets?.some((t) => t.code === c)) || s.number === code.trim())).first();
}

const RETENTION_DAYS = 30;

/**
 * Keep the terminal's store bounded: synced and refused sales older than 30 days, uploaded gate scans older than that,
 * and other days' gate whitelists are removed. Pending sales, the sale just before the oldest pending one (its chain
 * anchor) and the last sale (the chain head) are always kept.
 */
export async function prune(db: PosDB, now: Date = new Date()): Promise<void> {
  const cutoff = new Date(now.getTime() - RETENTION_DAYS * 86_400_000).toISOString();
  const firstPending = (await db.sales.where("status").equals("pending").sortBy("n"))[0]?.n ?? Infinity;
  const last = (await db.sales.orderBy("n").last())?.n ?? 0;
  const keepFrom = Math.min(firstPending, last) - 1;
  await db.sales.where("n").below(keepFrom).filter((s) => s.status !== "pending" && s.createdAt < cutoff).delete();
  await db.scans.where("synced").equals(1).filter((x) => x.scannedAt < cutoff).delete();
  const today = `whitelist:${todayIST(now)}`;
  await db.meta.where("key").startsWith("whitelist:").filter((m) => m.key !== today).delete();
}
